<script setup>
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { formatDateTime } from '../../utils/format_offer';
  import { useOfferStore } from '../../stores/OfferStore';
  import { useBrowseCartStore } from '../../stores/BrowseCartStore';
  import Loader from '../ui/Loader.vue';

  const { t } = useI18n();
  const storeOffer = useOfferStore();
  const cartStore = useBrowseCartStore();
  const props = defineProps({
    data: {
      type: Object,
      required: true
    },
    status: {
      type: String,
      required: true
    }
  });

  const newPrice = ref(props.data.offer.price);
  const loading = ref(false);

  const handleFormatCode = (event) => {
    const code = event.target.getAttribute('data-code');
    const parent = event.target.closest('.order-details__code-content');
    parent.setAttribute('data-code', code);
  }

  const handleUpdateOffer =  async (kind) => {
    const cartId = props.data.cart.id
    const offerId = props.data.offer.id
    loading.value = true;
    await storeOffer.updateOperationOffers(cartId, offerId, kind, newPrice.value);
    if (storeOffer.operation_response.success) {
      cartStore.cart.status = storeOffer.operation_response.cart_status;
    }
    loading.value = false;
    if(kind == 'update_price') {
      const regex = /[-+]?[0-9]*\.?[0-9]+/g;
      props.data.offer.cart_admin_price = props.data.offer.cart_admin_price.replace(regex, newPrice.value);
    }
  }

  const formattedInput = ref('');

  const handleInput = (event) => {
    const regex = /^\d+(\.\d{0,2})?$/;
    const cartButton = document.querySelector('.cart-cleanup--update-price');
    const input = event.target.value;
    if (regex.test(input) || input === '') {
      formattedInput.value = input;
      newPrice.value = input;
    } else {
      event.target.value = formattedInput.value;
    }
    if (formattedInput.value === '') {
      cartButton.classList.add('disabled');
      cartButton.disabled = true;
    } else {
      cartButton.classList.remove('disabled');
      cartButton.disabled = false;
    }
  };
</script>

<template>
  <div class="container">
    <div class="alert alert-light p-0" v-for="order in data.orders" :key="order.id">
      <div class="row p-2">
        <div class="col-10">
          <div>
            {{ t('offers.cart_orders.order_id') }}: <strong>{{ order.order_id }}</strong>
          </div>
          <div>
            {{ t('offers.cart_orders.book') }}: <strong>{{ order.book }}</strong>
          </div>
          <div>
            {{ t('offers.cart_orders.copy_uuid') }}: <strong>{{ order.copy_uuid }}</strong>
          </div>
        </div>
        <div class="col-2">
          <button class="btn w-100 h-100" data-bs-toggle="collapse" :href="'#collapse-' + order.id" role="button" aria-expanded="false" :aria-controls="'collapse-' + order.id">
            <div class="d-flex align-items-center justify-content-end">
              <i class="bi bi-chevron-down" aria-hidden="true"></i>
            </div>
          </button>
        </div>
      </div>
      <div class="collapse p-2" :id="'collapse-' + order.id">
        <div>
          {{ t('offers.cart_orders.created_at') }}: <strong>{{ formatDateTime(order.created_at) }}</strong>
        </div>
        <div>
          {{ t('offers.cart_orders.provider_publication') }}: <strong>{{ data.offer.item.provider }}</strong>
        </div>
        <div>
          {{ t('offers.cart_orders.provider_offer') }}: <strong>{{ order.provider }}</strong>
        </div>
        <div>
          {{ t('offers.cart_orders.distributor_offer') }}: <strong>{{ order.distributor }}</strong>
        </div>

        <div class="row mb-1">
          <div class="col">
            <strong>URL</strong>
          </div>
        </div>
        <div class="row mb-2">
          <div class="col">
            <div class="p-2 text-white bg-dark rounded">
              <code>
                <span>{{ order.query_url }}</span>
              </code>
            </div>
          </div>
        </div>

        <div class="order-details__code-content" data-code="row">
          <div class="row f-flex align-items-center mb-1">
            <div class="col-auto">
              <strong>{{ t('offers.cart_orders.query') }}</strong>
            </div>
            <div class="col text-end">
              <div class="btn-group btn-group-sm" role="group">
                <input @click="handleFormatCode" type="radio" class="btn-check" :name="`btnradioq-${order.id}`" :id="`btnradio1-${order.id}`" autocomplete="off" checked data-code="raw">
                <label class="btn btn-outline-primary" :for="`btnradio1-${order.id}`">{{ t('offers.cart_orders.raw') }}</label>

                <input @click="handleFormatCode" type="radio" class="btn-check" :name="`btnradioq-${order.id}`" :id="`btnradio2-${order.id}`" autocomplete="off" data-code="formatted">
                <label class="btn btn-outline-primary" :for="`btnradio2-${order.id}`">{{ t('offers.cart_orders.formatted') }}</label>
              </div>
            </div>
          </div>
          <div class="row mb-2">
            <div class="col">
              <div class="p-2 text-white bg-dark rounded">
                <code>
                  <span class="order-details__code--raw">{{ order.order_query }}</span>
                  <span class="order-details__code--formatted" v-html="order.query_strings_code"></span>
                </code>
              </div>
            </div>
          </div>
        </div>

        <div class="order-details__code-content" data-code="row">
          <div class="row f-flex align-items-center mb-1">
            <div class="col-auto">
              <strong>{{ t('offers.cart_orders.response') }}</strong>
            </div>
            <div class="col text-end">
              <div class="btn-group btn-group-sm" role="group">
                <input @click="handleFormatCode" type="radio" class="btn-check" :name="`btnradior-${order.id}`" :id="`btnradio3-${order.id}`" autocomplete="off" checked data-code="raw">
                <label class="btn btn-outline-primary" :for="`btnradio3-${order.id}`">{{ t('offers.cart_orders.raw') }}</label>

                <input @click="handleFormatCode" type="radio" class="btn-check" :name="`btnradior-${order.id}`" :id="`btnradio4-${order.id}`" autocomplete="off" data-code="formatted">
                <label class="btn btn-outline-primary" :for="`btnradio4-${order.id}`">{{ t('offers.cart_orders.formatted') }}</label>
              </div>
            </div>
          </div>
          <div class="row mb-2">
            <div class="col">
              <div class="p-2 text-white bg-dark rounded">
                <code>
                  <span class="order-details__code--raw">{{ order.order_response }}</span>
                  <span class="order-details__code--formatted" v-html="order.hash_code"></span>
                </code>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <template v-if="loading">
      <Loader />
    </template>
    <template v-else>
      <div class="alert alert-danger" v-if="data.offer.cart_admin">
        <div class="row mb-2">
          <div class="col">
            <button @click.prevent="handleUpdateOffer('remove')" v-if="!data.cart_item.any_copies" type="button" class="btn btn-danger ms-0">
              {{  t('offers.admin.remove') }}
            </button>
            <button @click.prevent="handleUpdateOffer('retry')" type="button" class="btn btn-danger">
              {{  t('offers.admin.retry') }}
            </button>
            <button @click.prevent="handleUpdateOffer('disable')" type="button" class="btn btn-danger">
              {{ t('offers.admin.disable') }}
            </button>
          </div>
        </div>
        <div class="row">
          <div class="col">
            <div class="mb-1">{{ t('offers.admin.current_price', { price: data.offer.cart_admin_price }) }}</div class="mb-1">
            {{ t('offers.admin.new_price') }}
            <input type="text" name="new_price" v-model="newPrice" @input="handleInput">
            <button @click.prevent="handleUpdateOffer('update_price')" name="button" type="button" class="btn btn-danger cart-cleanup--update-price">{{ t('offers.admin.update_price') }}</button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
  code {
    color: white;
  }

  .container {
    min-width: 1000px;
  }

  .order-details__code-content[data-code="row"] .order-details__code--formatted {
    display: none;
  }

  .order-details__code--raw {
    line-break: anywhere;
  }
</style>
